import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Modal from "../../../../parts/modal-new";
import SelectDays from "../../../../parts/inputs/select-days";
import submit from "submit-form";
import Select from "../../../../parts/inputs/select";
import SvgIcon from "../../../../parts/SvgIcon";

const AccountabilityOnlyTemplates = ({
  templateId,
  userToLoadInformation,
  organizationOwner,
}) => {
  const { t } = useTranslation("coaching-portal");
  const templates =
    organizationOwner?.accountabilityTemplates?.templates ||
    userToLoadInformation?.accountabilityTemplates?.templates ||
    [];
  const defaultTemplate =
    organizationOwner?.accountabilityTemplates?.defaultTemplate ??
    userToLoadInformation?.accountabilityTemplates?.defaultTemplate;

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [mobileOptions, setMobileOptions] = useState([]);

  useEffect(() => {
    if (!templates?.length) return;

    const defaultTemplateOb = templates.find(
      (template) => template._id === defaultTemplate
    );

    const templateById =
      templateId && templates.find((template) => template._id === templateId);

    const options = templates.map((template) => ({
      value: template._id,
      label: template.name,
    }));

    const initialSelection = templateById || defaultTemplateOb || templates[0];

    setMobileOptions(options);
    setSelectedTemplate(initialSelection);
  }, [templates, defaultTemplate, templateId]);

  const handleSetDefault = () => {
    const isDefault = selectedTemplate._id === defaultTemplate;
    const url = isDefault
      ? "/actions/daily-accountability/remove-default"
      : "/actions/daily-accountability/set-default";

    submit(url, {
      method: "POST",
      body: {
        templateId: selectedTemplate._id,
        userToRequest: organizationOwner?._id || userToLoadInformation._id,
      },
    });
  };

  const removeTemplate = (id) => {
    if (confirm(t("dailyAccountabilityTemplates.confirmDeleteTemplate"))) {
      submit("/actions/daily-accountability/remove-template", {
        method: "POST",
        body: {
          templateId: id,
          userToRequest: organizationOwner?._id || userToLoadInformation._id,
        },
      });
    }
  };

  return (
    <Modal
      toClear={["section", "templateId"]}
      className={"accountability-template-modal"}
      title={t("allTemplates.accountabilityOnlyTemplates.title")}
    >
      <div
        className={`body ${!templates.length ? "body-for-modal-no-templates" : ""}`}
      >
        {!templates.length ? (
          <div className="modal-no-templates">
            <h4>
              {t(
                "allTemplates.accountabilityOnlyTemplates.noTemplatesAvailable"
              )}
            </h4>
            <p className="p5">
              {t("allTemplates.accountabilityOnlyTemplates.createFirstHint")}
            </p>
            <a
              role="button"
              data-button-type="primary"
              href="/actions/refresh?modal[type]=add-accountability-template"
            >
              {t("allTemplates.accountabilityOnlyTemplates.addNewTemplate")}
            </a>
          </div>
        ) : (
          <>
            <aside className="force-mobile-hidden">
              <p className="p5">
                {t("allTemplates.accountabilityOnlyTemplates.selectTemplate")}
              </p>
              <ul>
                {templates?.map((item, i) => {
                  return (
                    <li
                      key={`${item.name}-${i}-column`}
                      aria-current={
                        selectedTemplate?._id === item._id ? true : undefined
                      }
                      onClick={() => setSelectedTemplate(item)}
                    >
                      <p className="p1">{item.name}</p>
                    </li>
                  );
                })}
              </ul>
            </aside>
            <section className="mobile-templates-list only-show-mobile">
              <p className="p5">
                {t("allTemplates.accountabilityOnlyTemplates.selectTemplate")}
              </p>
              <div className="select">
                <Select
                  instanceId="accountability-only-templates-select"
                  autosize={false}
                  matchPos="start"
                  clearable={false}
                  options={mobileOptions}
                  simpleValue
                  onChange={(value) => {
                    const template = templates.find(
                      (templ) => templ._id === value
                    );
                    setSelectedTemplate(template);
                  }}
                  value={selectedTemplate?.name}
                />
              </div>
            </section>
            {selectedTemplate && (
              <section>
                <header>
                  <h5>{selectedTemplate.name}</h5>
                  <div>
                    <button
                      type="button"
                      onClick={() => removeTemplate(selectedTemplate._id)}
                      data-button-type="text-white"
                      data-icon-only
                      sz="medium"
                    >
                      <img src="/img/new-icons/red-trash.svg" alt="delete" />
                    </button>
                    <a
                      className="force-mobile-hidden"
                      role="button"
                      data-button-type="tertiary"
                      href={`/actions/refresh?modal[type]=add-accountability-template&templateId=${selectedTemplate._id}&section=accountability`}
                    >
                      {t(
                        "allTemplates.accountabilityOnlyTemplates.editTemplate"
                      )}
                    </a>
                    <a
                      className="only-show-mobile"
                      href={`/actions/refresh?modal[type]=add-accountability-template&templateId=${selectedTemplate._id}&section=accountability`}
                      role="button"
                      data-button-type="tertiary"
                    >
                      <img src="/img/new-icons/edit-pencil.svg" alt="edit" />
                    </a>
                  </div>
                </header>
                {selectedTemplate.list.map((acc, i) => (
                  <article key={`${acc.title}-${i}`}>
                    <aside>
                      <p className="s3">{acc.title}</p>
                      <p className="p5">{acc.description}</p>
                    </aside>
                    <section>
                      <SelectDays
                        days={acc.days}
                        startingCalendarDay={
                          userToLoadInformation?.startingCalendarDay ?? 0
                        }
                      />
                    </section>
                  </article>
                ))}
              </section>
            )}
          </>
        )}
      </div>
      {selectedTemplate && (
        <>
          {/* footer for desktop - no Save button */}
          <footer className="acc-template-footer force-mobile-hidden">
            <aside>
              <a
                href="/actions/refresh?modal[type]=add-accountability-template"
                role="button"
                data-button-type="outline"
              >
                {t("allTemplates.accountabilityOnlyTemplates.addNewTemplate")}
              </a>
              <div
                onClick={handleSetDefault}
                role="button"
                data-button-type="outline"
              >
                <SvgIcon
                  src={`/img/new-icons/${
                    selectedTemplate?._id === defaultTemplate ? "" : "un"
                  }checked-square.svg`}
                  noFilterOnLight={
                    selectedTemplate?._id === defaultTemplate ? true : false
                  }
                />
                <p className="p4">
                  {t("allTemplates.accountabilityOnlyTemplates.setAsDefault")}
                </p>
              </div>
            </aside>
          </footer>
          {/* footer for mobile - no Save button */}
          <footer className="acc-template-footer force-only-show-mobile">
            <div className="div-wrapper-footer-acc">
              <div onClick={handleSetDefault}>
                <SvgIcon
                  src={`/img/new-icons/${
                    selectedTemplate?._id === defaultTemplate ? "" : "un"
                  }checked-square.svg`}
                />
                <p className="p4">
                  {t("allTemplates.accountabilityOnlyTemplates.setAsDefault")}
                </p>
              </div>
              <div>
                <a
                  href="/actions/refresh?modal[type]=add-accountability-template&section=accountability"
                  role="button"
                  data-button-type="outline"
                >
                  {t("allTemplates.accountabilityOnlyTemplates.addNewTemplate")}
                </a>
              </div>
            </div>
          </footer>
        </>
      )}
    </Modal>
  );
};

export default AccountabilityOnlyTemplates;
